import { useCallback, useEffect, useState } from "react";

import { SessionEvent } from "../domain/sessionEvent";
import { SessionEventsAPI } from "../infrastructure/api/sessionEvents.api";

export type SessionEventsSource = {
    sessionId: string;
    /** Read through the centre's route, as one of its administrators. */
    centerId?: string;
};

export type SessionEventsState = {
    events: SessionEvent[];
    loading: boolean;
    failed: boolean;
    retry: () => void;
};

/** A session's timeline, oldest first. */
export function useSessionEvents({ sessionId, centerId }: SessionEventsSource): SessionEventsState {
    const [events, setEvents] = useState<SessionEvent[]>([]);
    const [loading, setLoading] = useState(true);
    const [failed, setFailed] = useState(false);
    const [attempt, setAttempt] = useState(0);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setFailed(false);
        const request = centerId
            ? SessionEventsAPI.forCenterSession(centerId, sessionId)
            : SessionEventsAPI.forSession(sessionId);
        request
            .then((items) => {
                if (!cancelled) setEvents(items);
            })
            .catch(() => {
                if (!cancelled) setFailed(true);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [sessionId, centerId, attempt]);

    const retry = useCallback(() => setAttempt((count) => count + 1), []);

    return { events, loading, failed, retry };
}
